<template>
  <div class="header-main-kids">
    <div class="header-main-kids__bar">
      <div class="container container-main">
        <div class="header-main-kids__row">
          <div class="header-main-kids__start">
            <button
              type="button"
              class="header-main-kids__burger"
              :aria-expanded="isMenuOpen"
              aria-controls="header-kids-menu"
              @click="toggleMenu"
            >
              <span class="sr-only">{{ isMenuOpen ? $t("common.close") : $t("common.menu") }}</span>
              <span
                class="header-main-kids__burger-icon"
                :class="{ 'is-open': isMenuOpen }"
                aria-hidden="true"
              >
                <span />
                <span />
                <span />
              </span>
            </button>
            <HeaderNavKids @click="closeMenu" />
          </div>
          <div class="header-main-kids__actions">
            <HeaderProfileList
              v-if="!isInFrame()"
              nav-like
            />
            <LocalesSwitcher
              nav-like
              list-position="left"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="isMenuOpen"
      id="header-kids-menu"
      class="header-main-kids__overlay"
    >
      <button
        type="button"
        class="header-main-kids__backdrop"
        :aria-label="$t('common.close')"
        @click="closeMenu"
      />
      <div class="header-main-kids__panel">
        <div class="container container-main">
          <HeaderNavKids panel />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from "vue"
import { useRoute } from "vue-router"
import { isInFrame } from "@academy/utils"

import HeaderNavKids from "./header-nav/HeaderNavKids.vue"
import HeaderProfileList from "@/components/header/header-nav/HeaderProfilesList.vue"
import LocalesSwitcher from "@/components/LocalesSwitcher.vue"

defineProps({
  isOpenMenu: {
    type: Boolean,
    required: true,
  },
  isMobileSearch: {
    type: Boolean,
    required: true,
  },
})

defineEmits({
  "toogle-menu": null,
  "toogle-mobile-search": null,
  "close-mobile-menu": null,
  "open-partner-form": null,
  search: (value) => typeof value === "string",
})

const route = useRoute()
const isMenuOpen = ref(false)

const closeMenu = () => {
  isMenuOpen.value = false
}

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const onKeydown = (event) => {
  if (event.key === "Escape") {
    closeMenu()
  }
}

const onViewportChange = (event) => {
  if (event.matches) {
    closeMenu()
  }
}

watch(isMenuOpen, (open) => {
  document.body.style.overflow = open ? "hidden" : ""
})

watch(() => route.fullPath, closeMenu)

let desktopMedia

onMounted(() => {
  window.addEventListener("keydown", onKeydown)
  desktopMedia = window.matchMedia("(min-width: 1024px)")
  desktopMedia.addEventListener("change", onViewportChange)
})

onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown)
  desktopMedia?.removeEventListener("change", onViewportChange)
  document.body.style.overflow = ""
})
</script>

<style lang="scss" scoped>
.header-main-kids {
  position: relative;
  z-index: 40;
  background: var(--color-cream);

  &__bar {
    position: relative;
    z-index: 3;
    padding-top: 35px;
    padding-bottom: 35px;
    background: var(--color-cream);
    border-bottom: 1px solid #e2e1dd;
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 55px;
  }

  &__start {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  &__actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    height: 55px;
  }

  &__burger {
    display: none;
    box-sizing: border-box;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #17263f;
    cursor: pointer;
  }

  &__burger-icon {
    position: relative;
    display: block;
    width: 22px;
    height: 16px;

    span {
      position: absolute;
      left: 0;
      display: block;
      width: 22px;
      height: 2px;
      border-radius: 1px;
      background: currentColor;
      transition: transform 0.2s ease, opacity 0.2s ease, top 0.2s ease;
    }

    span:nth-child(1) {
      top: 0;
    }

    span:nth-child(2) {
      top: 7px;
    }

    span:nth-child(3) {
      top: 14px;
    }

    &.is-open {
      span:nth-child(1) {
        top: 7px;
        transform: rotate(45deg);
      }

      span:nth-child(2) {
        opacity: 0;
      }

      span:nth-child(3) {
        top: 7px;
        transform: rotate(-45deg);
      }
    }
  }

  &__overlay {
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    z-index: 2;
  }

  &__backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100vh;
    z-index: 1;
    border: 0;
    padding: 0;
    background: rgba(23, 38, 63, 0.4);
    cursor: pointer;
  }

  &__panel {
    position: relative;
    z-index: 2;
    padding: 8px 0 28px;
    background: var(--color-cream);
    border-bottom: 1px solid #e2e1dd;
  }
}

@media (max-width: 1279px) and (min-width: 1024px) {
  .header-main-kids {
    &__bar {
      padding-top: 24px;
      padding-bottom: 24px;
    }

    &__row {
      gap: 12px;
    }
  }
}

@media (max-width: 1023px) {
  .header-main-kids {
    &__bar {
      padding-top: 16px;
      padding-bottom: 16px;
    }

    &__burger {
      display: inline-flex;
    }
  }
}
</style>
